import {
  ColumnMapping,
  DataStoreField,
  MAP_CREATE_NEW,
  MAP_SKIP,
} from "@/datastore/types";
import { inferType } from "@/datastore/utils/typeInfer";

function normalize(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]/g, "");
}

/**
 * Propose a default mapping of CSV columns onto existing fields.
 * Matching is by normalized name (case/punctuation-insensitive); every field
 * is used at most once; unmatched CSV columns default to "create new field"
 * with an inferred type from the sample values of that column.
 */
export function defaultColumnMapping(
  header: string[],
  sampleRows: string[][],
  fields: DataStoreField[]
): ColumnMapping[] {
  const remaining = new Map(fields.map((f) => [normalize(f.name), f]));
  return header.map((csvColumn, colIdx) => {
    const samples = sampleRows.map((r) => r[colIdx] ?? "");
    const inferredType = inferType(samples);
    const match = remaining.get(normalize(csvColumn));
    if (match) {
      remaining.delete(normalize(csvColumn));
      return { csvColumn, target: match.id, inferredType };
    }
    return { csvColumn, target: MAP_CREATE_NEW, inferredType };
  });
}

/** Mapping is valid when at least one column lands on a real or new field. */
export function isMappingUsable(mapping: ColumnMapping[]): boolean {
  return mapping.some((m) => m.target !== MAP_SKIP);
}

/** A field must not receive two CSV columns. */
export function duplicateTargets(mapping: ColumnMapping[]): string[] {
  const seen = new Map<string, number>();
  mapping
    .filter((m) => m.target !== MAP_SKIP && m.target !== MAP_CREATE_NEW)
    .forEach((m) => seen.set(m.target, (seen.get(m.target) ?? 0) + 1));
  return [...seen.entries()].filter(([, n]) => n > 1).map(([id]) => id);
}
